/**
 * Messaging layout shell.
 *
 * Desktop: conversation list | active conversation | optional context panel,
 * all three proportioned by the design system (no `calc(100vh - 200px)` magic
 * numbers, no oversized containers, no duplicate DOM trees).
 *
 * Mobile: true single-pane page navigation — the list OR the thread, never
 * both, with the thread taking the full viewport (WhatsApp / Telegram model).
 * The previous implementation rendered the sidebar twice (once for desktop,
 * once for mobile), doubling listeners and DOM nodes.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';
import './messaging.css';
import { IconButton, MessagingTag } from './primitives';
import { useViewportHeight } from './hooks';

export interface MessagingLayoutProps {
  /** Left rail — conversation list. */
  list: React.ReactNode;
  /** Center pane — active conversation (or an empty state). */
  thread: React.ReactNode;
  /** Right rail — contextual details, shown from desktop (≥1024px) up or as slide-over. */
  context?: React.ReactNode;
  /** Controls if context rail is open (for tablet slide-over / desktop toggle). */
  isContextOpen?: boolean;
  onToggleContext?: () => void;
  /** True when a conversation is open: drives mobile pane switching. */
  hasSelection?: boolean;
  /**
   * The open conversation's list-row id (its `data-conversation-id`).
   * Used only to return keyboard focus to that row when the mobile
   * single-pane view switches back to the list (Chat Phase 4).
   */
  selectedConversationId?: string | null;
  /** Removes the outer border/radius when the parent already provides a card. */
  flush?: boolean;
  className?: string;
  /**
   * Colour scheme for this subtree. Light only: the owner's rule
   * (2026-10-25, absolute) is no dark theme anywhere in the portal, so
   * messaging.css resolves [data-color-scheme='auto'] to the light token
   * set for every user — the former OS-dark override block was removed
   * in SYS-04 (UI/UX Phase 2, Stage 1 item 9). 'auto' remains the default
   * value, but it no longer follows the OS: every chat surface renders
   * the same light UI.
   */
  colorScheme?: 'light' | 'auto';
  style?: React.CSSProperties;
}

export function MessagingLayout({
  list,
  thread,
  context,
  isContextOpen = true,
  onToggleContext,
  hasSelection = false,
  selectedConversationId,
  flush = false,
  className = '',
  colorScheme = 'auto',
  style,
}: MessagingLayoutProps) {
  const listPaneRef = React.useRef<HTMLDivElement>(null);
  const threadPaneRef = React.useRef<HTMLDivElement>(null);
  const prevSelectionRef = React.useRef(hasSelection);
  const lastSelectedIdRef = React.useRef<string | null>(null);
  if (selectedConversationId) lastSelectedIdRef.current = selectedConversationId;

  /* Focus follows the pane switch on the mobile single-pane layout
     (Chat Phase 4): below the two-pane breakpoint the list and thread
     are never both visible, so when a conversation opens, focus left
     on the (now display:none) row is lost to <body> — keyboard and
     screen-reader users land nowhere. Move focus into the thread pane
     on open, and back to the conversation's row on close. Desktop is
     deliberately untouched: both panes stay visible and focus belongs
     wherever the user put it. */
  React.useEffect(() => {
    const was = prevSelectionRef.current;
    prevSelectionRef.current = hasSelection;
    if (was === hasSelection) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    if (hasSelection) {
      threadPaneRef.current?.focus({ preventScroll: true });
      return;
    }
    const id = lastSelectedIdRef.current;
    const escaped =
      id && typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(id)
        : id?.replace(/["\\]/g, '');
    const row =
      (escaped &&
        listPaneRef.current?.querySelector<HTMLElement>(
          `[data-conversation-id="${escaped}"]`
        )) ||
      listPaneRef.current?.querySelector<HTMLElement>('[data-conversation-id]');
    row?.focus({ preventScroll: true });
  }, [hasSelection]);

  return (
    <div
      className={`msg-scope msg-shell ${flush ? 'msg-shell--flush' : ''} ${className}`.trim()}
      data-color-scheme={colorScheme}
      style={style}
    >
      <div className="msg-panes" data-mobile-view={hasSelection ? 'thread' : 'list'}>
        <div className="msg-pane-list" ref={listPaneRef}>{list}</div>
        <div className="msg-pane-thread" ref={threadPaneRef} tabIndex={-1}>{thread}</div>
        {context && (
          <aside className={`msg-pane-context ${isContextOpen ? 'msg-pane-context--open' : 'msg-pane-context--closed'}`}>
            {context}
          </aside>
        )}
      </div>
    </div>
  );
}

/* ─── Thread frame ────────────────────────────────────────────────────────── */

export interface ThreadHeaderProps {
  title: string;
  subtitle?: string;
  avatar?: React.ReactNode;
  /** Shown on mobile (and whenever onBack is provided) to return to the list. */
  onBack?: () => void;
  backLabel?: string;
  tags?: Array<{ label: string; tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' }>;
  actions?: React.ReactNode;
  statusDot?: 'online' | 'offline' | null;
  /**
   * Split-view surfaces keep the list visible on desktop, so the back control
   * is only meaningful on mobile. CSS hides it instead of a JS breakpoint so
   * there is no flash on first paint or after a rotation.
   */
  hideBackOnDesktop?: boolean;
}

export function ThreadHeader({
  title,
  subtitle,
  avatar,
  onBack,
  backLabel = 'Back to conversations',
  tags,
  actions,
  statusDot = null,
  hideBackOnDesktop = false,
}: ThreadHeaderProps) {
  return (
    <header className="msg-header">
      {onBack && (
        <IconButton
          label={backLabel}
          onClick={onBack}
          className={hideBackOnDesktop ? 'msg-hide-desktop' : undefined}
        >
          <ChevronLeft size={21} strokeWidth={2.2} />
        </IconButton>
      )}

      {avatar}

      <div className="msg-header-info">
        <h3 className="msg-header-title">
          <span>{title}</span>
          {tags?.map((tag) => (
            <MessagingTag key={tag.label} tone={tag.tone}>
              {tag.label}
            </MessagingTag>
          ))}
        </h3>
        {subtitle && (
          <p className="msg-header-sub">
            {statusDot && (
              <span
                className={`msg-presence ${statusDot === 'online' ? 'msg-presence--online' : ''}`.trim()}
                style={{ position: 'static', width: 7, height: 7, border: 'none' }}
              />
            )}
            <span>{subtitle}</span>
          </p>
        )}
      </div>

      {actions && <div className="msg-header-actions">{actions}</div>}
    </header>
  );
}

/**
 * Vertical frame for a single conversation: sticky header, one scroll region,
 * sticky composer. Use inside MessagingLayout's `thread` slot or standalone.
 */
export function ThreadFrame({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`msg-thread ${className}`.trim()}>{children}</div>;
}

/* ─── Fullscreen overlay (mobile page mode / desktop modal) ───────────────── */

export interface MessagingOverlayProps {
  children: React.ReactNode;
  onClose: () => void;
  label?: string;
  /** Same Phase 2 decision as MessagingLayout: 'auto' (follows the OS). */
  colorScheme?: 'light' | 'auto';
}

export function MessagingOverlay({
  children,
  onClose,
  label = 'Conversation',
  colorScheme = 'auto',
}: MessagingOverlayProps) {
  // Keeps --msg-vh in sync for browsers where 100dvh is unavailable/unreliable.
  useViewportHeight();

  const overlay = (
    <>
      <div className="msg-overlay-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="msg-scope msg-overlay"
        data-color-scheme={colorScheme}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </div>
    </>
  );

  /*
   * ── Why this is a portal ────────────────────────────────────────────────
   * A `position: fixed` element is positioned relative to the viewport ONLY
   * while no ancestor establishes a containing block. Any ancestor carrying
   * `transform`, `filter`, `backdrop-filter`, `perspective`, `contain` or
   * `will-change` silently changes that: the overlay is then positioned
   * relative to that ancestor instead.
   *
   * That is exactly what happened on the applicant inbox at mobile widths.
   * Measured in headless Chromium at 390x844 with a transformed ancestor:
   *
   *     overlay   top 1298  bottom 2142   inViewport: false
   *     header    top 1298                (off-screen)
   *     composer  top 2078                (off-screen)
   *     painted at top of screen    -> the GLOBAL app header
   *     painted at bottom of screen -> the bottom tab bar
   *
   * i.e. the message list was reachable by scrolling, but the conversation
   * header and the composer were rendered far below the fold, and the global
   * chrome stayed visible because the overlay never covered it.
   *
   * Portalling to <body> removes the entire class of bug: no ancestor can
   * trap the overlay, whatever future markup wraps the chat.
   */
  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}

/* ─── Context panel helpers ───────────────────────────────────────────────── */

export function ContextSection({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="msg-context-section">
      <h4 className="msg-context-label">
        {icon}
        {label}
      </h4>
      {children}
    </section>
  );
}

export function ContextRow({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <p className="msg-context-row">
      {icon}
      <span>{children}</span>
    </p>
  );
}
